import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Notice from "components/tw/notice";
import { btnDangerCls, btnPrimaryCls, btnSecondaryCls, inputCls, labelCls } from "components/tw/classes";
import useIsPhone from "features/files/use-is-phone";
import { Phase, QueueWalk, WalkEntry } from "./use-queue-walk";

const MIN_DECLINE_REASON_LENGTH = 3;
// Skip and decline are plain text on a desktop; on a phone they get a border and a 44 px height to hit.
const QUIET_BUTTON =
  "text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11 max-md:rounded-lg max-md:border max-md:px-3 max-md:py-2";
// After the walk moved on, the main button stays off for this long on a phone, so a late or double tap
// on the previous document's button cannot sign the next one before it has been seen.
const HOLD_AFTER_MOVING_ON_MS = 1500;

/** The line the footer shows for the document's state, if there is one. */
function lineOf(phase: Phase): { tone: "success" | "warning" | "critical"; messageKey: string } | null {
  switch (phase.name) {
    case "moving-on":
      return { tone: "success", messageKey: phase.messageKey };
    case "dokobit-opened":
      return { tone: "warning", messageKey: "queue_msg_dokobit_opened" };
    case "failed":
      return { tone: "critical", messageKey: phase.messageKey };
    default:
      return null;
  }
}

function primaryLabelKey(entry: WalkEntry): string {
  if (entry.kind === "review") return "queue_action_review";
  return entry.item.signType === "Dokobit" ? "tasks_action_sign_dokobit" : "tasks_action_sign";
}

/**
 * The buttons under the open document. Give it a `key` of the document so the reason typed
 * for one document never carries over to the next. Whether the decline form is open is the
 * sheet's to know, because on a phone the form takes the document's place.
 */
export default function SheetFooter({
  walk,
  entry,
  phase,
  canAct,
  isDeclining,
  onDecliningChange,
  justMovedOn,
}: {
  walk: QueueWalk;
  entry: WalkEntry;
  phase: Phase;
  canAct: boolean;
  isDeclining: boolean;
  onDecliningChange: (isDeclining: boolean) => void;
  /** This document came up after another one was dealt with. */
  justMovedOn: boolean;
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [reason, setReason] = useState("");
  const [isMainButtonHeld, setIsMainButtonHeld] = useState(justMovedOn && isPhone);
  const line = lineOf(phase);

  useEffect(() => {
    if (!isMainButtonHeld) return undefined;
    const timer = window.setTimeout(() => setIsMainButtonHeld(false), HOLD_AFTER_MOVING_ON_MS);
    return () => window.clearTimeout(timer);
  }, [isMainButtonHeld]);

  return (
    <footer className="space-y-3 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      {line && <Notice tone={line.tone}>{t(line.messageKey)}</Notice>}

      {phase.name === "dokobit-opened" && (
        <button type="button" className={`${btnPrimaryCls} max-md:min-h-11 max-md:w-full`} onClick={walk.continueWalk}>
          {t("queue_action_next")}
        </button>
      )}

      {phase.name !== "dokobit-opened" && !isDeclining && (
        // On a phone: the main button across the full width, the two quieter ones under it side by side.
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 max-md:grid max-md:grid-cols-2 max-md:gap-3">
          <button
            type="button"
            className={`${btnPrimaryCls} max-md:col-span-2 max-md:min-h-11`}
            disabled={!canAct || isMainButtonHeld}
            onClick={walk.confirm}
          >
            {t(primaryLabelKey(entry))}
          </button>
          <button
            type="button"
            className={`${QUIET_BUTTON} text-zinc-600 ${entry.kind === "sign" ? "" : "max-md:col-span-2"}`}
            disabled={!canAct}
            onClick={walk.skip}
          >
            {t("queue_action_skip")}
          </button>
          {entry.kind === "sign" && (
            <button
              type="button"
              className={`${QUIET_BUTTON} text-red-600`}
              disabled={!canAct}
              onClick={() => onDecliningChange(true)}
            >
              {t("queue_action_decline")}
            </button>
          )}
        </div>
      )}

      {phase.name !== "dokobit-opened" && isDeclining && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            walk.decline(reason);
          }}
        >
          <p className="mb-2 text-sm font-medium text-gray-900 md:hidden">{entry.item.title}</p>
          <label className="block">
            <span className={labelCls}>{t("queue_decline_reason")}</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={inputCls}
            />
          </label>
          <div className="mt-2 flex gap-2 max-md:gap-3">
            <button
              type="submit"
              className={`${btnDangerCls} max-md:min-h-11`}
              disabled={!canAct || reason.trim().length < MIN_DECLINE_REASON_LENGTH}
            >
              {t("queue_action_decline_confirm")}
            </button>
            <button
              type="button"
              className={`${btnSecondaryCls} max-md:min-h-11`}
              disabled={!canAct}
              onClick={() => {
                onDecliningChange(false);
                setReason("");
              }}
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      )}
    </footer>
  );
}
